/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Periods } from 'cio/utils/metrics/common';
import EnvironmentModel from 'cio/models/environment';
import { tracked } from '@glimmer/tracking';
import { underscore } from '@ember/string';
import actionInfo from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import { action } from '@ember/object';
import moment from 'moment-timezone';
import { Grouping } from 'cio/utils/metrics/resolution';
import { Metric } from 'cio/utils/metrics/models/metric';
import { TimeRange } from 'cio/utils/time';

interface DashboardPageExpandableMetricsCardArgs {
  title: string;
  measures: { name: string; isRate: boolean }[];
  chartColor: string;
  environmentModel: EnvironmentModel;
  timeRange: TimeRange;
  resolution: Periods;
  grouping: Grouping;
  isLoading: boolean;
  isUpdating: boolean;
  channels: { label: string; value: string; icon: string }[];
  deliveryMetrics: Metric[];
  selectedChannel: string;
  onChannelChanged: (channel: string) => unknown;
  onTrackChartChanged: (data: Record<string, any>) => unknown;
  onTrackChartHover: (data: Record<string, any>) => unknown;
  onTrackNavigate: (data: Record<string, any>) => unknown;
}

export default class DashboardPageExpandableMetricsCard extends Component<DashboardPageExpandableMetricsCardArgs> {
  @tracked expandedMeasure: string | null;

  constructor(owner: unknown, args: DashboardPageExpandableMetricsCardArgs) {
    super(owner, args);

    this.expandedMeasure = this.args.measures[0]?.name ?? null;
  }

  get utcTimeRange() {
    return {
      start: moment.tz(`${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
      end: moment.tz(`${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
    };
  }

  get analysisQueryParams() {
    return {
      dataTypesParam: ['campaign', 'broadcast', 'newsletter'].join('_'),
      tab: 'chart',
      chartType: 'aggregate',
      channels: this.args.selectedChannel,
      measures: this.args.measures.map((m) => `delivery_metrics.${underscore(m.name)}`).join('-'),
      startTime: this.utcTimeRange.start.format('YYYY-MM-DD'),
      endTime: this.utcTimeRange.end.format('YYYY-MM-DD'),
      resolution: this.args.resolution,
      chartGrouping: this.args.grouping,
    };
  }

  get metricForChannel() {
    if (this.args.deliveryMetrics.length === 0) {
      return null;
    }

    return this.args.deliveryMetrics.find((metric) => metric.actionType === `${this.args.selectedChannel}_action`);
  }

  get deliveryMetricsAvailableForChannel() {
    let availableMeasures: string[] = [];

    let info = actionInfo(`${this.args.selectedChannel}_action` as ActionType);

    let permanentStates = info.deliveryStates ? [...info.deliveryStates] : [];

    if (info.deliveryActions) {
      availableMeasures.push(...info.deliveryActions);
    }

    availableMeasures.push(...permanentStates);
    if (this.args.selectedChannel === 'email') {
      availableMeasures.push('delivered');
    }

    return availableMeasures.uniq();
  }

  @action
  onChannelChanged(channel: string) {
    this.args.onChannelChanged(channel);

    this.args.onTrackChartChanged({
      editType: 'channel',
      card: underscore(this.args.title),
      channel,
      metric: this.expandedMeasure,
    });
  }

  @action
  onExpandedMeasureChanged(measure: string) {
    this.expandedMeasure = measure;

    this.args.onTrackChartChanged({
      editType: 'chart',
      card: underscore(this.args.title),
      channel: this.args.selectedChannel,
      metric: measure,
    });
  }

  @action
  trackNavigate() {
    this.args.onTrackNavigate({
      card: underscore(this.args.title),
      destination: 'analysis',
      channel: this.args.selectedChannel,
      metric: this.expandedMeasure,
    });
  }
}
